import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Empty } from 'antd';
import { getSuggestedKeywords } from 'Explore/Services/Collaborations';
import { RotateCcw, Calendar, Search } from 'lucide-react';
import { Skeleton } from 'components/ui/skeleton';
import { Button } from 'components/ui/button';

/**
 * Unified Insufficient Data Notification component for Collaboration.
 * Uses Ant Design's official Empty component for an authentic, professional
 * enterprise placeholder matching the rest of the application.
 */
const InsufficientDataNotice = ({
  pubCount = 0,
  entity,
  filters = {},
  setFilters,
  title = "Insufficient Data to Calculate Metrics",
  description,
}) => {
  const currentYear = new Date().getFullYear();
  const keywords = filters?.subjectArea || [];

  const { data: suggestedKws, isLoading: isSuggestLoading } = useQuery({
    queryKey: ['collab-insufficient-suggest', entity?.id, entity?.type, filters?.yearRange?.[0], filters?.yearRange?.[1], keywords.join(',')],
    queryFn: () => getSuggestedKeywords({
      entity_id: entity?.id,
      entity_type: entity?.type,
      publication_year_start: filters?.yearRange?.[0],
      publication_year_end: filters?.yearRange?.[1],
      exclude_keywords: keywords,
    }),
    staleTime: 10 * 60 * 1000,
    enabled: !!entity?.id && keywords.length > 0,
  });

  const suggestions = suggestedKws?.keywords?.filter(kw => !keywords.includes(kw)) || [];

  const handleKeywordClick = (kw) => {
    if (setFilters) {
      setFilters(prev => ({
        ...prev,
        subjectArea: [kw],
      }));
    }
  };

  const handleClearSubjectFilter = () => {
    if (setFilters) {
      setFilters(prev => ({
        ...prev,
        subjectArea: [],
      }));
    }
  };

  const handleResetTimeRange = () => {
    if (setFilters) {
      setFilters(prev => ({
        ...prev,
        yearRange: [2014, currentYear],
      }));
    }
  };

  const isTimeRangeRestricted = filters?.yearRange && (filters.yearRange[0] > 2014 || filters.yearRange[1] < currentYear);

  return (
    <div className="w-full min-h-[500px] flex flex-col items-center justify-center text-center p-8 sm:p-12 bg-white border border-gray-300 rounded-lg shadow-2xs animate-in fade-in duration-300">
      <Empty
        image={Empty.PRESENTED_IMAGE_DEFAULT}
        imageStyle={{ height: 100, marginBottom: 16 }}
        description={
          <div className="flex flex-col items-center gap-2 max-w-md mx-auto">
            <h3 className="text-base font-semibold text-gray-900 m-0">
              {title}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed max-w-[440px] m-0">
              {description || (
                <>
                  The selected entity has only <strong className="font-semibold text-gray-800">{pubCount}</strong> publication{pubCount !== 1 ? 's' : ''} for the active filters. A minimum of 10 publications is required to calculate reliable collaboration metrics, institutional reach, and network trends.
                </>
              )}
            </p>
          </div>
        }
      >
        {/* Quick Action Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
          {isTimeRangeRestricted && (
            <Button
              size="sm"
              onClick={handleResetTimeRange}
              className="h-9 px-4 gap-2 bg-explore hover:bg-explore/90 text-white font-medium text-xs rounded-md shadow-xs transition-all cursor-pointer"
            >
              <Calendar className="w-4 h-4" />
              Expand to Full Date Range (2014–{currentYear})
            </Button>
          )}

          {keywords.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearSubjectFilter}
              className="h-9 px-4 gap-2 text-gray-700 border-gray-300 hover:bg-gray-50 text-xs rounded-md cursor-pointer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Clear Subject Filter
            </Button>
          )}

          {keywords.length === 0 && !isTimeRangeRestricted && (
            <p className="text-xs text-gray-400 max-w-sm m-0 leading-relaxed">
              Tip: Search for another entity or adjust filters in the left sidebar to view collaboration analytics.
            </p>
          )}
        </div>

        {/* Related Topic Suggestion Chips (when keywords filter is active) */}
        {keywords.length > 0 && (
          <div className="flex flex-col items-center gap-2.5 mt-6 pt-5 border-t border-gray-100 w-full max-w-lg mx-auto">
            <span className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
              <Search className="w-3.5 h-3.5 text-gray-400" />
              Or broaden search with related research topics:
            </span>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
              {isSuggestLoading ? (
                [...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-7 w-24 rounded bg-slate-100" />
                ))
              ) : suggestions.length > 0 ? (
                suggestions.slice(0, 6).map(kw => (
                  <button
                    key={kw}
                    onClick={() => handleKeywordClick(kw)}
                    className="border border-dashed border-gray-300 hover:border-explore hover:bg-slate-50 text-explore text-xs px-3 py-1.5 rounded transition-all cursor-pointer font-medium"
                  >
                    {kw}
                  </button>
                ))
              ) : (
                <span className="text-xs italic text-gray-400">No keyword suggestions available.</span>
              )}
            </div>
          </div>
        )}
      </Empty>
    </div>
  );
};

export default InsufficientDataNotice;
